import React, { useState, useEffect } from "react";
import styles from "./styles.module.scss";
import cx from "classnames";
import PropTypes from "prop-types";
import DecimalInput from "../DecimalInput";
import t from "../../../utils/translate";
import {
  stonePoundsToStone,
  stonePoundsToPounds,
  stoneToStonePounds
} from "../../../utils/weightUnits";
import { ScreenReaderLabel } from "@ww-digital/web-foundations-react/modules";
import FormErrorMessage from "../../FormErrorMessage";
import isOutOfBounds from "../../../utils/isOutOfBounds";
import useEvent from "../../../utils/useEvent";
import { capitalize } from "lodash";

function StonePoundsInput({
  name = "weight-input",
  market,
  label = {
    stone: {
      placeholder: "",
      appended: ""
    },
    pounds: {
      placeholder: "",
      appended: ""
    }
  },
  value,
  min,
  max,
  onChange = () => {},
  labelClassName,
  errorClassName,
  fieldsetWrapperClassName,
  capitalizeLabel,
  isShortUnit
}) {
  const defaultValues = stoneToStonePounds(value);
  const [stone, setStone] = useState(defaultValues.stone);
  const [pounds, setPounds] = useState(defaultValues.pounds);

  const [errors, setErrors] = useState(() => {
    return {
      stone: false,
      pounds: false,
      total: false
    };
  });

  const { listen } = useEvent("SettingsValidation");
  useEffect(() => {
    return listen(() => {
      const hasStoneError =
        stone === null || isOutOfBounds(stone, min.stone, max.stone);
      const hasPoundsError =
        pounds === null || isOutOfBounds(pounds, min.pounds, max.pounds);
      const totalVal = stonePoundsToPounds({ stone, pounds });
      const hasTotalError = isOutOfBounds(totalVal, 0, max.total);

      setErrors({
        stone: hasStoneError,
        pounds: hasPoundsError,
        total: hasTotalError
      });
    });
  }, [stone, pounds, min, max, listen]);

  const isInvalid = errors => Object.values(errors).includes(true);

  const handleStone = (_, stoneVal) => {
    setStone(stoneVal);
    const hasStoneError =
      stoneVal === null || isOutOfBounds(stoneVal, min.stone, max.stone);
    const totalVal = stonePoundsToPounds({ stone: stoneVal, pounds });
    const hasTotalError = isOutOfBounds(totalVal, 0, max.total);
    const newErrorState = {
      ...errors,
      stone: hasStoneError,
      total: hasTotalError
    };
    setErrors(newErrorState);
    onChange(
      isInvalid(newErrorState) ? null : stonePoundsToStone(stoneVal, pounds)
    );
  };

  const handlePounds = (_, poundsVal) => {
    poundsVal = poundsVal > max.pounds ? max.pounds : poundsVal;
    setPounds(poundsVal);
    const hasPoundsError = isOutOfBounds(poundsVal, min.pounds, max.pounds);
    const totalVal = stonePoundsToPounds({ pounds: poundsVal, stone });
    const hasTotalError = isOutOfBounds(totalVal, 0, max.total);
    const newErrorState = {
      ...errors,
      pounds: hasPoundsError,
      total: hasTotalError
    };
    setErrors(newErrorState);
    onChange(
      isInvalid(newErrorState) ? null : stonePoundsToStone(stone, poundsVal)
    );
  };

  const poundsId = `${name}-pounds`;

  return (
    <>
      <div
        className={cx(
          styles.stonePoundsContainer,
          styles.appendedLabel,
          fieldsetWrapperClassName
        )}
      >
        <div className={styles.imperialStone}>
          <DecimalInput
            id={name}
            aria-required={true}
            market={market}
            mask="00"
            error={errors.stone || errors.total}
            defaultValue={stone ?? ""}
            onChange={handleStone}
          />
          <label
            className={cx(styles.tailingLabel, labelClassName)}
            htmlFor={name}
          >
            {isShortUnit
              ? label.stone.appendedShort
              : capitalizeLabel
                ? capitalize(label.stone.appended)
                : label.stone.appended}
          </label>
          {errors.stone && (
            <ScreenReaderLabel role="alert">
              {`Stones input of ${stone} must be between ${min.stone} and ${max.stone}`}
            </ScreenReaderLabel>
          )}
        </div>
        <div className={styles.imperialPound}>
          <DecimalInput
            id={poundsId}
            aria-required={true}
            market={market}
            mask="00.0"
            error={errors.pounds || errors.total}
            defaultValue={pounds ?? ""}
            onChange={handlePounds}
            max={max.pounds}
          />
          <label
            className={cx(styles.tailingLabel, labelClassName)}
            htmlFor={poundsId}
          >
            {isShortUnit
              ? label.pounds.appendedShort
              : capitalizeLabel
                ? capitalize(label.pounds.appended)
                : label.pounds.appended}
          </label>
          {errors.pounds && (
            <ScreenReaderLabel role="alert">
              {`Pounds input of ${pounds} must be between ${min.pounds} and ${max.pounds}`}
            </ScreenReaderLabel>
          )}
        </div>
      </div>

      {/* data-error is used to target and hide SingleWeightInput error messages in WeightGoal */}
      {(errors.total || errors.pounds || errors.stone) && (
        <div data-error>
          <FormErrorMessage
            role="alert"
            data-test="weight-input-error"
            className={errorClassName}
          >
            {t("WEIGHT_INVALID")}
          </FormErrorMessage>
        </div>
      )}
    </>
  );
}

StonePoundsInput.propTypes = {
  name: PropTypes.string,
  market: PropTypes.string,
  min: PropTypes.shape({
    stone: PropTypes.number,
    pounds: PropTypes.number
  }),
  max: PropTypes.shape({
    stone: PropTypes.number,
    pounds: PropTypes.number
  }),
  onChange: PropTypes.func
};

export default StonePoundsInput;
